import {useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import {Form, Formik} from 'formik';
import * as yup from 'yup';

import Button from 'components/Button';
import {ButtonColor, ButtonType} from 'components/Button/types';
import {Input} from 'components/FormElements';
import {ModalBody, ModalFooter} from 'components/Modal';
import {MAX_MINT_AMOUNT} from 'constants/general';
import {createMint} from 'dispatchers/mints';
import {ToastType} from 'enums';
import {AppDispatch, Currency, SFC} from 'types';
import {handleFormikAPIError} from 'utils/forms';
import {displayToast} from 'utils/toasts';

import * as S from './Styles';

interface ComponentProps {
  close(): void;
  currency: Currency;
  onSuccess?: () => void;
}

const MintModal: SFC<ComponentProps> = ({className, close, currency, onSuccess}) => {
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const initialValues = useMemo(
    () => ({
      amount: '',
    }),
    [],
  );

  const validationSchema = useMemo(() => {
    return yup.object().shape({
      amount: yup
        .number()
        .typeError('Amount must be a number')
        .required('Amount is required')
        .positive('Amount must be positive')
        .integer('Amount must be a whole number')
        .max(MAX_MINT_AMOUNT, `Amount cannot exceed ${MAX_MINT_AMOUNT.toLocaleString()}`),
    });
  }, []);

  const handleSubmit = async (values: typeof initialValues, helpers: any) => {
    setSubmitting(true);
    try {
      await dispatch(createMint({currency: currency.id, amount: parseInt(values.amount)}));
      displayToast(
        `Successfully minted ${parseInt(values.amount).toLocaleString()} ${currency.ticker}`,
        ToastType.SUCCESS,
      );
      if (onSuccess) onSuccess();
      close();
    } catch (error: any) {
      const responseData = error?.response?.data;

      if (responseData?.amount && Array.isArray(responseData.amount)) {
        const amountErrors = responseData.amount;
        if (amountErrors.length > 0 && amountErrors[0]?.message) {
          helpers.setFieldError('amount', amountErrors[0].message);
        } else {
          helpers.setFieldError('amount', 'Invalid amount');
        }
      } else {
        const errorMessage = responseData?.error || 'Error minting currency';
        handleFormikAPIError(error, helpers, errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <S.Modal className={className} close={close} header={`Mint ${currency.ticker}`}>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
        {({dirty, errors, isValid, touched}) => (
          <Form>
            <ModalBody>
              <Input errors={errors} label="Amount" name="amount" touched={touched} type="number" />
            </ModalBody>

            <ModalFooter>
              <Button color={ButtonColor.secondary} onClick={close} text="Cancel" type={ButtonType.button} />
              <Button
                dirty={dirty}
                disabled={submitting}
                isSubmitting={submitting}
                isValid={isValid}
                text="Mint"
                type={ButtonType.submit}
              />
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </S.Modal>
  );
};

export default MintModal;
